import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import TeamMapper from '../../mappers/team.mapper';
import {
  AccessControlFilterConfigModel,
  AssignmentLimitModel,
  AssignmentRuleModel,
  TeamMemberChangeModel,
  TeamMemberNodeModel,
  TeamModel
} from '../../models/team.model';
import {
  AssignmentLimitUiModel,
  AssignmentRuleUiModel,
  TeamMemberChangeUiModel,
  TeamMemberUiModel,
  TeamUiModel
} from '../../ui-models/team.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';

/**
 * Teams, the reporting tree, and the routing configuration that hangs off them.
 *
 * In the platform layer because two things read it: TeamModule manages it, and
 * every lead screen that offers an assignee needs the member list.
 */
const MODULE_NAME = 'team';

const fail = (response: unknown, fn: unknown) => {
  const res = response as { status?: number; data?: { errorCode?: string }; problem?: unknown };
  return errorHelper(
    Number(res.status),
    res.data && res.data.errorCode ? String(res.data.errorCode || '') : String(res.problem || ''),
    MODULE_NAME,
    (fn as { displayName?: string }).displayName as string
  );
};

export function getTeams(): Promise<TeamUiModel[] | ErrorInfo> {
  return api.searchTeams({}).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapTeamToUIList(response.data.data as TeamModel[]);
    }
    return fail(response, api.searchTeams);
  });
}

export function getTeamById(teamId: string): Promise<TeamUiModel | ErrorInfo> {
  return api.getTeamById(teamId).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapTeamToUI(response.data.data as TeamModel);
    }
    return fail(response, api.getTeamById);
  });
}

export function createTeam(team: Partial<TeamModel>): Promise<string | ErrorInfo> {
  return api.createTeam(team).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return (response.data.data as { id: string }).id;
    }
    return fail(response, api.createTeam);
  });
}

export function updateTeam(
  teamId: string,
  team: Partial<TeamModel>
): Promise<TeamUiModel | ErrorInfo> {
  return api.updateTeam(teamId, team).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapTeamToUI(response.data.data as TeamModel);
    }
    return fail(response, api.updateTeam);
  });
}

export function deleteTeam(teamId: string): Promise<boolean | ErrorInfo> {
  return api.deleteTeam(teamId).then((response) => {
    if (response.ok) return true;
    return fail(response, api.deleteTeam);
  });
}

/**
 * The filter keys and values a grant may name.
 *
 * Fetched so the picker and the server validate against one list — a grant is
 * an authorization surface, and an unvalidated key is a query nobody approved.
 */
export function getAccessControlConfig(): Promise<
  AccessControlFilterConfigModel | ErrorInfo
> {
  return api.getAccessControlConfig().then((response) => {
    if (response.ok && response.data && response.data.data) {
      return response.data.data as AccessControlFilterConfigModel;
    }
    return fail(response, api.getAccessControlConfig);
  });
}

export function getTeamMemberTree(
  filter: Record<string, unknown> = {}
): Promise<TeamMemberUiModel[] | ErrorInfo> {
  return api.getTeamMemberTree(filter).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapTreeToUI(response.data.data as TeamMemberNodeModel[]);
    }
    return fail(response, api.getTeamMemberTree);
  });
}

/** The flat list — for assignee pickers, which want people not a hierarchy. */
export function getTeamMembers(
  query: Record<string, unknown> = {}
): Promise<TeamMemberUiModel[] | ErrorInfo> {
  return api.searchTeamMembers(query).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return (response.data.data as TeamMemberNodeModel[]).map((member) =>
        TeamMapper.mapMemberToUI({ ...member, children: [] }, false)
      );
    }
    return fail(response, api.searchTeamMembers);
  });
}

export function createTeamMember(
  member: Record<string, unknown>
): Promise<string | ErrorInfo> {
  return api.createTeamMember(member).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return (response.data.data as { id: string }).id;
    }
    return fail(response, api.createTeamMember);
  });
}

export function updateTeamMember(
  userId: string,
  member: Record<string, unknown>
): Promise<boolean | ErrorInfo> {
  return api.updateTeamMember(userId, member).then((response) => {
    if (response.ok) return true;
    return fail(response, api.updateTeamMember);
  });
}

export function archiveTeamMember(userId: string): Promise<boolean | ErrorInfo> {
  return api.archiveTeamMember(userId).then((response) => {
    if (response.ok) return true;
    return fail(response, api.archiveTeamMember);
  });
}

export function recoverTeamMember(userId: string): Promise<boolean | ErrorInfo> {
  return api.recoverTeamMember(userId).then((response) => {
    if (response.ok) return true;
    return fail(response, api.recoverTeamMember);
  });
}

export function getTeamMemberHistory(
  userId: string
): Promise<TeamMemberChangeUiModel[] | ErrorInfo> {
  return api.getTeamMemberHistory(userId).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapChangeToUIList(response.data.data as TeamMemberChangeModel[]);
    }
    return fail(response, api.getTeamMemberHistory);
  });
}

export function getAssignmentLimits(): Promise<AssignmentLimitUiModel[] | ErrorInfo> {
  return api.getAssignmentLimits().then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapLimitToUIList(response.data.data as AssignmentLimitModel[]);
    }
    return fail(response, api.getAssignmentLimits);
  });
}

export function upsertAssignmentLimit(
  role: string,
  maxActiveLeads: number | null
): Promise<boolean | ErrorInfo> {
  return api.upsertAssignmentLimit({ role, maxActiveLeads }).then((response) => {
    if (response.ok) return true;
    return fail(response, api.upsertAssignmentLimit);
  });
}

export function getAssignmentRules(): Promise<AssignmentRuleUiModel[] | ErrorInfo> {
  return api.getAssignmentRules().then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TeamMapper.mapRuleToUIList(response.data.data as AssignmentRuleModel[]);
    }
    return fail(response, api.getAssignmentRules);
  });
}

export function getRuleCriteriaKeys(): Promise<string[] | ErrorInfo> {
  return api.getRuleCriteriaKeys().then((response) => {
    if (response.ok && response.data && response.data.data) {
      return response.data.data as string[];
    }
    return fail(response, api.getRuleCriteriaKeys);
  });
}
